import { v4 } from 'uuid'
import { action, observable, makeObservable, computed } from 'mobx'
import { task } from 'mobx-task'
import ModalViewModel from 'infra/ModalViewModel'
import { ScopeChangeInventoryParams, ScopeChangeParams } from 'jobs/JobStore'
import { maxLength, validateElements } from 'utils/validation-utils'
import { required, validationContext } from 'validx'
import InventoryStore from 'admin/InventoryStore'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { Id } from 'types/definitions'
import { ViewModelList } from 'infra/ViewModelList'
import Job, { JobScopeItem } from 'jobs/Job'
import InventoryItemRequestQueryFilterBuilder, {
  InventoryItemArchivedFilterOptions,
  InventoryItemProVisibilityFilterOptions,
} from 'admin/inventory/InventoryItemRequestQueryFilterBuilder'
import { InventoryItemForProviders } from '@taxfyle/api-internal/internal/inventory_item_pb'
import { InventoryItemPricing } from 'admin/inventory/InventoryItem'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { switchMap } from 'rxjs'
import { shareLatest } from 'utils/rx-util'
import { tap } from 'rxjs/operators'
import { GetCurrentMember } from 'iam/SessionStore'

/**
 * View Model for initiating a scope change request.
 */
export default class RequestScopeChangesViewModel extends ModalViewModel {
  @observable state = RequestScopeChangesState.RequestingChanges

  readonly maxReasonLength = 280

  /**
   * Scope change inventory items view models.
   */
  private readonly scopeChangeInventoryItems

  /**
   * Custom scope changes view models
   */
  @observable scopeChangeCustomItems: ScopeChangeCustomItem[] = []

  /**
   * The error.
   */
  @observable error = ''

  /**
   * The reason.
   */
  @observable reason = ''

  /**
   * Serch term for item selection
   */
  searchText = new SearchTextViewModel()

  /**
   * Selected scope change inventory items
   */
  @observable
  selectedInventoryItems: ScopeChangeInventoryItem[] = []

  @computed
  get itemsSelected() {
    return (
      this.scopeChangeCustomItems.length !== 0 ||
      this.selectedInventoryItems.length !== 0
    )
  }

  @computed
  get quantitiesChanged() {
    return (
      (this.selectedInventoryItems.length > 0 &&
        this.selectedInventoryItems.some(
          (item) => item.newQuantity !== item.currentQuantity,
        )) ||
      (this.scopeChangeCustomItems.length > 0 &&
        this.scopeChangeCustomItems.every((item) => item.quantity > 0))
    )
  }

  @computed
  get canCreateCustomJobScopeItemRequest() {
    return this.getCurrentMember().hasAllPermissions(
      'ADMIN_JOB_SCOPE_CHANGE_MANAGE',
      'ADMIN_JOB_SCOPE_CHANGE_RESOLVE',
    )
  }

  /**
   * Inventory item scroll provider
   */
  inventoryScrollProvider =
    new InfiniteScrollProvider<InventoryItemForProviders.AsObject>({
      limit: 100,
      startFetchTaskAsPending: true,
      fetchItems: (paging) =>
        this.inventoryStore.fetchInventoryItemsForProvider(
          this.workspaceId,
          paging.limit ?? 100,
          this.searchText.value,
          paging.after ?? undefined,
          new InventoryItemRequestQueryFilterBuilder()
            .withPriceTypes([InventoryItemPricing.Flat])
            .withArchivedStatus(InventoryItemArchivedFilterOptions.ACTIVE)
            .withProVisibilityStatus(
              InventoryItemProVisibilityFilterOptions.VISIBLE,
            )
            .forJobType(this.job.jobType)
            .build(),
          'name',
        ),
    })

  /**
   * Server query pipeline.
   */
  query$ = this.searchText.debouncedValue$.pipe(
    switchMap(() => this.inventoryScrollProvider.fetch()),
    shareLatest(),
  )

  /**
   * Validation context for scope changes step.
   */
  readonly scopeChangesValidationStep = validationContext(this, {
    scopeChangeCustomItems: [
      validateElements('Some scope changes are invalid.'),
    ],
  })

  /**
   * Validation context for submission step.
   */
  readonly submissionValidationStep = validationContext(this, {
    reason: [
      required('Reason is required.'),
      maxLength(
        this.maxReasonLength,
        'Reason must be less than 280 characters.',
      ),
    ],
  })

  constructor(
    public job: Job,
    private inventoryStore: InventoryStore,
    private workspaceId: Id,
    private _requestScopeChanges: (
      reason: string,
      items: ScopeChangeParams[],
      inventoryItems: ScopeChangeInventoryParams[],
    ) => Promise<unknown>,
    private _revealEvents: () => void,
    private getCurrentMember: GetCurrentMember,
  ) {
    super()
    makeObservable(this)
    this.scopeChangeInventoryItems = new ViewModelList({
      models: () => this.inventoryScrollProvider.items.slice(),
      create: (model) =>
        new ScopeChangeInventoryItem(
          model,
          this.job.jobScopeItems,
          this.selectedInventoryItems
            .map((i) => i.versionId)
            .includes(model.versionId),
          (item) => {
            if (item.selected) {
              this.selectedInventoryItems = [
                item,
                ...this.selectedInventoryItems,
              ]
            } else {
              this.selectedInventoryItems = this.selectedInventoryItems.filter(
                (i) => i.versionId !== item.versionId,
              )
            }
          },
        ),
    })
    this.show$.subscribe(this.reset)
    this.query$.pipe(this.whileShowing()).subscribe()
    this.show$.pipe(
      tap(() => this.activate()),
      switchMap(() => this.close$),
      tap(() => this.deactivate()),
    )
  }

  /**
   * Activate VM and fetch all items.
   */
  @action.bound
  activate() {
    this.selectedInventoryItems = []
    this.scopeChangeInventoryItems.activate()
  }

  /**
   * Deactivates the view model.
   */
  @action.bound
  deactivate() {
    this.scopeChangeInventoryItems.deactivate()
  }

  /**
   * Send scope change request.
   */
  request = task.resolved(async () => {
    if (!this.validateSubmissionStep()) {
      return
    }

    const items = this.scopeChangeCustomItems.map((item) => ({
      name: item.name,
      quantity: item.quantity,
    }))

    // Only request the job scope change for inventory items that have a different new quantity
    const inventoryItems = this.selectedInventoryItems
      .filter((item) => item.newQuantity !== item.currentQuantity)
      .map((item) => ({
        versionId: item.versionId,
        quantity: item.newQuantity,
      }))

    try {
      await this._requestScopeChanges(this.reason, items, inventoryItems)
      this.setState(RequestScopeChangesState.Success)
    } catch (err) {
      console.error('Request scope changes failed.', err)
      this.error = err.message
      this.setState(RequestScopeChangesState.Failed)
    }
  })

  /**
   * Total count for selected inventory items including custom items.
   */
  @computed
  get totalSelectedInventoryItemsCount() {
    return (
      this.selectedInventoryItems.length + this.scopeChangeCustomItems.length
    )
  }

  /**
   * The selected inventory items that have updated quantities
   */
  @computed
  get relevantSelectedInventoryItems() {
    return this.selectedInventoryItems.filter(
      (i) => i.currentQuantity !== i.newQuantity,
    )
  }

  /**
   * Indicates if the service items are empty.
   */
  @computed
  get isServiceItemsEmpty() {
    return this.serviceItems.length === 0
  }

  /**
   * The service items
   */
  @computed
  get serviceItems() {
    return this.scopeChangeInventoryItems.items
  }

  /**
   * Reason text box characters.
   */
  @computed
  get reasonCharsRemaining() {
    if (this.reason) {
      return this.maxReasonLength - this.reason.length
    }

    return this.maxReasonLength
  }

  /**
   * Resets the state.
   */
  @action.bound
  reset() {
    this.request.reset()
    this.submissionValidationStep.reset()
    this.scopeChangesValidationStep.reset()
    this.setState(RequestScopeChangesState.RequestingChanges)

    this.reason = ''
    this.error = ''
    this.searchText.clear()
    this.scopeChangeCustomItems = []
  }

  /**
   * Indicates if the submission step is valid.
   */
  @action.bound
  validateSubmissionStep() {
    return this.submissionValidationStep.reset().validate().isValid
  }

  /**
   * Moves to the next step if valid.
   */
  @action.bound
  moveToSubmissionStep() {
    if (this.scopeChangesValidationStep.reset().validate().isValid) {
      this.setState(RequestScopeChangesState.ReviewingChanges)
    }
  }

  /**
   * Moves to the next step if valid.
   */
  @action.bound
  moveToQuantityStep() {
    if (this.scopeChangesValidationStep.reset().validate().isValid) {
      this.setState(RequestScopeChangesState.UpdatingQuantity)
    }
  }

  /**
   * Sets the request scope changes state.
   *
   * @param state
   */
  @action.bound
  setState(state: RequestScopeChangesState) {
    this.state = state
  }

  /**
   * Closes the dialog and reveals the events tab.
   */
  @action.bound
  goToEvents() {
    this.close()
    this._revealEvents()
  }

  /**
   * Adds a new custom scope change
   */
  @action.bound
  addScopeChange() {
    this.scopeChangeCustomItems.push(new ScopeChangeCustomItem())
  }

  /**
   * Removes a previously added custom scope change.
   *
   * @param key
   */
  @action.bound
  removeScopeChange(key: string) {
    const pos = this.scopeChangeCustomItems.findIndex(
      (item) => item.key === key,
    )
    this.scopeChangeCustomItems.splice(pos, 1)
  }
}

/**
 * Describes the state of the view model.
 */
export enum RequestScopeChangesState {
  /**
   * Waiting for the user to fill in all the scope changes required.
   */
  RequestingChanges,
  /**
   * Waiting for the user to update all the quantities.
   */
  UpdatingQuantity,
  /**
   * Waiting for the user to confirm that they want to request scope changes.
   */
  ReviewingChanges,
  /**
   * CS will be notified when the request is sent.
   */
  Success,
  /**
   * Something went wrong while executing the request scope changes command, could be a
   * validation error or something transient.
   */
  Failed,
}

/**
 * View Model for a scope change item.
 */
export class ScopeChangeCustomItem {
  @observable key = ''

  @observable name = ''

  @observable quantity = 1

  /**
   * Validation context for custom scope change.
   */
  readonly validation = validationContext(this, {
    name: [required('Service name is required.')],
    quantity: [
      (opts) =>
        opts.value >= 1 || opts.value <= -1 || 'Quantity cannot be zero.',
    ],
  })

  constructor() {
    makeObservable(this)
    this.key = v4()
  }

  @action.bound
  incrementQuantity() {
    this.quantity += 1
  }

  @action.bound
  decrementQuantity() {
    if (this.quantity === 1) {
      return
    }

    this.quantity -= 1
  }

  @action.bound
  setQuantity(value: string) {
    this.quantity = parseInt(value, 10) || 1
  }

  @action.bound
  setName(value: string) {
    this.name = value
  }
}

export class ScopeChangeInventoryItem {
  @observable versionId = ''

  @observable name = ''

  @observable description = ''

  @observable selected = false

  /**
   * The new quantity of the item
   */
  @observable newQuantity = 1

  /**
   * The current quantity of the item
   */
  @observable currentQuantity = 0

  /**
   * Validation context for custom scope change.
   */
  readonly validation = validationContext(this, {
    newQuantity: [
      (opts) =>
        opts.value >= 1 || opts.value <= -1 || 'Quantity cannot be zero.',
    ],
  })

  constructor(
    item: InventoryItemForProviders.AsObject,
    previousItems: JobScopeItem[],
    isSelected: boolean,
    private onSelectedChange: (item: ScopeChangeInventoryItem) => void,
  ) {
    const previousItem = previousItems.find(
      (c) => c.inventory_item_id === item.id,
    )

    this.versionId = previousItem?.inventory_item_version_id ?? item.versionId
    this.name = previousItem?.name ?? item.name
    this.description = previousItem?.description ?? item.description
    this.currentQuantity = previousItem?.quantity ?? 0
    // Let's start the quantity editor at a previous quantity if we have
    this.newQuantity = previousItem?.quantity ?? 1
    this.selected = isSelected
    makeObservable(this)
  }

  @action.bound
  incrementQuantity() {
    this.newQuantity += 1
  }

  @action.bound
  decrementQuantity() {
    if (this.newQuantity <= 0) {
      this.newQuantity = 0
    } else {
      this.newQuantity -= 1
    }
  }

  @action.bound
  setQuantity(value: string) {
    this.newQuantity = parseInt(value, 10) || 1
  }

  @action.bound
  toggle() {
    this.selected = !this.selected
    this.onSelectedChange(this)
  }
}
